'use client';

import { useEffect, type CSSProperties } from 'react';
import { useRouter } from 'next/navigation';
import { captureBoundaryException } from '@reopt/logger/sentry';
import * as Sentry from '@sentry/nextjs';

// Next.js 16 내장 에러 페이지(`app-error` 정적 500.html / `DefaultGlobalError`)를
// 한글로 재현한 것. global-error는 루트 레이아웃을 대체하므로 자체 <html>/<body>와
// 인라인 스타일이 필요하고, 빌드 타임 정적 500.html 생성에도 이 컴포넌트가 쓰인다
// (앱 CSS·폰트가 로드되지 않는 상황을 가정). 라이트/다크 테마 변수는 Next 원본 동일.

const styles = {
  container: {
    fontFamily:
      'system-ui,"Segoe UI",Roboto,Helvetica,Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji"',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  card: {
    marginTop: '-32px',
    maxWidth: '325px',
    padding: '32px 28px',
    textAlign: 'left'
  },
  icon: {
    marginBottom: '24px'
  },
  title: {
    fontSize: '24px',
    fontWeight: 500,
    letterSpacing: '-0.02em',
    lineHeight: '32px',
    margin: '0 0 12px 0',
    color: 'var(--next-error-title)'
  },
  message: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '21px',
    margin: '0 0 20px 0',
    color: 'var(--next-error-message)'
  },
  form: {
    margin: 0
  },
  buttonGroup: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  },
  button: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '32px',
    padding: '0 12px',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
    borderRadius: '6px',
    cursor: 'pointer',
    color: 'var(--next-error-btn-text)',
    background: 'var(--next-error-btn-bg)',
    border: 'var(--next-error-btn-border)'
  },
  buttonSecondary: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '32px',
    padding: '0 12px',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
    borderRadius: '6px',
    cursor: 'pointer',
    color: 'var(--next-error-btn-secondary-text)',
    background: 'var(--next-error-btn-secondary-bg)',
    border: 'var(--next-error-btn-secondary-border)'
  },
  digestFooter: {
    position: 'fixed',
    bottom: '32px',
    left: '0',
    right: '0',
    textAlign: 'center',
    fontFamily: 'ui-monospace,SFMono-Regular,"SF Mono",Menlo,Consolas,monospace',
    fontSize: '12px',
    lineHeight: '18px',
    fontWeight: 400,
    margin: '0',
    color: 'var(--next-error-digest)'
  }
} satisfies Record<string, CSSProperties>;

const themeCss = `
:root {
  --next-error-bg: #fff;
  --next-error-text: #171717;
  --next-error-title: #171717;
  --next-error-message: #171717;
  --next-error-digest: #666666;
  --next-error-btn-text: #fff;
  --next-error-btn-bg: #171717;
  --next-error-btn-border: none;
  --next-error-btn-secondary-text: #171717;
  --next-error-btn-secondary-bg: transparent;
  --next-error-btn-secondary-border: 1px solid rgba(0,0,0,0.08);
}
@media (prefers-color-scheme: dark) {
  :root {
    --next-error-bg: #0a0a0a;
    --next-error-text: #ededed;
    --next-error-title: #ededed;
    --next-error-message: #ededed;
    --next-error-digest: #a0a0a0;
    --next-error-btn-text: #0a0a0a;
    --next-error-btn-bg: #ededed;
    --next-error-btn-border: none;
    --next-error-btn-secondary-text: #ededed;
    --next-error-btn-secondary-bg: transparent;
    --next-error-btn-secondary-border: 1px solid rgba(255,255,255,0.14);
  }
}
body { margin: 0; color: var(--next-error-text); background: var(--next-error-bg); }
`;

function WarningIcon() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="-0.2 -1.5 32 32"
      fill="none"
      style={styles.icon}
    >
      <path
        d="M16.9328 0C18.0839 0.000116771 19.1334 0.658832 19.634 1.69531L31.4299 26.1309C32.0708 27.4588 31.1036 28.9999 29.6291 29H2.00215C0.527541 29 -0.439628 27.4588 0.201371 26.1309L11.9973 1.69531C12.4979 0.658823 13.5474 7.75066e-05 14.6984 0H16.9328ZM3.59493 26H28.0363L16.9328 3H14.6984L3.59493 26ZM15.8156 19C16.9202 19.0001 17.8156 19.8955 17.8156 21C17.8156 22.1045 16.9202 22.9999 15.8156 23C14.7111 23 13.8156 22.1046 13.8156 21C13.8156 19.8954 14.7111 19 15.8156 19ZM17.3156 16.5H14.3156V8.5H17.3156V16.5Z"
        fill="var(--next-error-title)"
      />
    </svg>
  );
}

export default function GlobalError({ error }: { error: Error & { digest?: string } }) {
  const router = useRouter();

  useEffect(() => {
    captureBoundaryException(Sentry, error, {
      boundary: 'web.global-error',
      browserState: {
        online: navigator.onLine,
        visibilityState: document.visibilityState
      }
    });
  }, [error]);

  const digest = error?.digest;
  // 서버 컴포넌트 오류는 digest가 붙는다. 클라이언트 오류와 안내 문구를 구분.
  const isServerError = !!digest;
  const message = isServerError
    ? '일시적인 문제가 발생했어요. 잠시 후 다시 시도해 주세요.'
    : '페이지를 새로 불러오거나 이전 화면으로 돌아가 주세요.';

  return (
    <html
      id="__next_error__"
      lang="ko"
    >
      <head>
        {/* global-error는 metadata export 불가 — React <title> 사용 */}
        <title>페이지를 불러오지 못했어요</title>
        <style dangerouslySetInnerHTML={{ __html: themeCss }} />
      </head>
      <body>
        <div style={styles.container}>
          <div style={styles.card}>
            <WarningIcon />
            <h1 style={styles.title}>페이지를 불러오지 못했어요</h1>
            <p style={styles.message}>{message}</p>
            <div style={styles.buttonGroup}>
              {/* form submit으로 새로고침 — JS 없는 정적 500.html에서도 동작 */}
              <form style={styles.form}>
                <button
                  type="submit"
                  style={styles.button}
                >
                  새로고침
                </button>
              </form>
              {!isServerError && (
                <button
                  type="button"
                  style={styles.buttonSecondary}
                  onClick={() => {
                    if (window.history.length > 1) {
                      window.history.back();
                    } else {
                      router.replace('/');
                    }
                  }}
                >
                  뒤로 가기
                </button>
              )}
            </div>
          </div>
        </div>
        {digest && <p style={styles.digestFooter}>오류 코드 {digest}</p>}
      </body>
    </html>
  );
}
